// Appointments. Clinic appointment requests (Restate booking saga) and the patient's legacy
// personal-calendar entries are both read, edited and deleted through
// `/appointment-requests/{id}`; the calendar list (`/me/calendar`) is only used to discover the
// legacy entries, which are normalized into the same shape here.
import api, {items, withIdempotencyKey} from './client';

const DEFAULT_DURATION_MS = 30 * 60000;

/** v1 appointment request -> the shape the appointment pages consume. */
export const toAppointment = (a) => ({
  ...a,
  note: a.notes,
  timezone: a.patient_timezone,
  appointment_date: a.start_time,
  reminder_alerts: a.alerts || [],
  source: 'clinic',
});

/**
 * Legacy personal-calendar entry (GET /me/calendar item) -> the new-style appointment shape, so
 * lists, detail and the editor treat both alike and every edit goes to `/appointment-requests`.
 */
export const fromCalendarEntry = (c) => {
  const startMs = Date.parse(c.appointment_date || c.start_time);
  const start = Number.isNaN(startMs) ? null : new Date(startMs).toISOString();
  const end = c.end_time
    ? new Date(c.end_time).toISOString()
    : start && new Date(startMs + DEFAULT_DURATION_MS).toISOString();
  const tz = c.timezone || c.patient_timezone || 'UTC';
  const alerts = (c.alerts || []).map((t, i) =>
    t && typeof t === 'object'
      ? t
      : {
          id: `${c.id}-alert-${i + 1}`,
          alert_time: t,
          delivery_channel: null,
          delivery_status: null,
          appointment_request_id: c.id,
        },
  );
  return toAppointment({
    id: c.id,
    doctor_id: null,
    doctor_name: null,
    clinic_division_id: null,
    start_time: start,
    end_time: end,
    patient_timezone: tz,
    status: 'confirmed',
    notes: c.note ?? c.notes ?? '',
    email_alert: Boolean(c.email_alert),
    sms_alert: Boolean(c.sms_alert),
    auto_assigned: false,
    alerts,
  });
};

/** Mark a normalized calendar entry as coming from the legacy calendar list. */
const asLegacy = (a) => ({...a, source: 'calendar', legacy: true});

const ALERT_LABEL_HOURS = {'1 hour': 1, '1 day': 24, '1 week': 168};

/** Reminder offsets ("1 hour", "1 day", "1 week") implied by alert instants before `start`. */
export const alertBeforeLabels = (start, alerts = []) => {
  const startMs = Date.parse(start);
  const found = new Set();
  for (const a of alerts) {
    const t = Date.parse(a && typeof a === 'object' ? a.alert_time : a);
    if (Number.isNaN(t) || Number.isNaN(startMs)) continue;
    const hours = Math.round((startMs - t) / 3600000);
    for (const [label, h] of Object.entries(ALERT_LABEL_HOURS)) if (h === hours) found.add(label);
  }
  return Object.keys(ALERT_LABEL_HOURS).filter((l) => found.has(l));
};

const alertLabels = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const sameSet = (a = [], b = []) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * PATCH body with only the fields that changed. Field names match the
 * POST /appointment-requests create body: `start_time`, `end_time`, `patient_timezone`,
 * `notes`, `email_alert`, `sms_alert`, `alert_before`. A new start keeps the original duration.
 * `alert_before` is sent whenever reminders must be re-planned (new time, channel or offsets),
 * because the API re-plans reminders from it.
 */
export const appointmentPatch = (original, next) => {
  const body = {};
  const origStart = Date.parse(original.start_time);
  const newStart = next.start_time ? Date.parse(next.start_time) : origStart;
  const timeChanged = !Number.isNaN(newStart) && newStart !== origStart;
  if (timeChanged) {
    const origEnd = Date.parse(original.end_time);
    const duration =
      Number.isNaN(origEnd) || Number.isNaN(origStart) || origEnd <= origStart
        ? DEFAULT_DURATION_MS
        : origEnd - origStart;
    body.start_time = new Date(newStart).toISOString();
    body.end_time = new Date(newStart + duration).toISOString();
  }
  if (next.patient_timezone && next.patient_timezone !== original.patient_timezone) {
    body.patient_timezone = next.patient_timezone;
  }
  if (next.notes !== undefined && (next.notes || '') !== (original.notes || '')) body.notes = next.notes;
  const channelChanged = ['email_alert', 'sms_alert'].filter(
    (k) => next[k] !== undefined && Boolean(next[k]) !== Boolean(original[k]),
  );
  for (const k of channelChanged) body[k] = Boolean(next[k]);
  const nextLabels = alertLabels(next.alert_before);
  const labelsChanged =
    next.alert_before !== undefined && !sameSet(nextLabels, alertLabels(original.alert_before));
  if (labelsChanged || (next.alert_before !== undefined && (timeChanged || channelChanged.length))) {
    body.alert_before = nextLabels;
  }
  return body;
};

/** Personal calendar entries in a date range (YYYY-MM-DD), normalized to the new-style shape. */
export const getAllAppointment = (order, dateRange) => {
  const params = new URLSearchParams({order: order === 'past' ? 'past' : 'upcoming'});
  if (dateRange && dateRange[0]) params.set('from', new Date(dateRange[0]).toISOString());
  if (dateRange && dateRange[1]) {
    const end = new Date(dateRange[1]);
    end.setDate(end.getDate() + 1);
    params.set('to', end.toISOString());
  }
  return api
    .get(`/me/calendar?${params}`)
    .then((r) => items(r.data).map((c) => asLegacy(fromCalendarEntry(c))));
};

export const getAppointmentById = (appointmentId) =>
  api.get(`/appointment-requests/${encodeURIComponent(appointmentId)}`).then((r) => toAppointment(r.data));

/** `opts.idempotencyKey` pins the key of one booking action (see hooks/useIdempotencyKeys). */
export const createAppointment = (d, opts = {}) => {
  const start = d.start_time || d.appointment_date;
  const body = {
    start_time: new Date(start).toISOString(),
    end_time: d.end_time
      ? new Date(d.end_time).toISOString()
      : new Date(new Date(start).getTime() + DEFAULT_DURATION_MS).toISOString(),
    patient_timezone: d.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    notes: d.note || d.notes || '',
    email_alert: Boolean(d.email_alert),
    sms_alert: Boolean(d.sms_alert),
    alert_before: alertLabels(d.alert_before),
  };
  const doctor = d.doctor || d.doctorId;
  const division = d.clinicDivision || d.clinic_division;
  if (doctor) body.doctor_id = doctor;
  if (division) body.clinic_division_id = division;
  return api
    .post('/appointment-requests', body, withIdempotencyKey(opts.idempotencyKey))
    .then((r) => toAppointment(r.data));
};

/**
 * PATCH /appointment-requests/{id} with an already-built body (see `appointmentPatch`).
 * The client adds the Idempotency-Key header.
 */
export const updateAppointment = (appointmentId, body) =>
  api
    .patch(`/appointment-requests/${encodeURIComponent(appointmentId)}`, body)
    .then((r) => toAppointment(r.data));

export const deleteAppointment = (appointmentId) =>
  api.delete(`/appointment-requests/${encodeURIComponent(appointmentId)}`);

/** Availability (free and occupied slots) for one day range, optionally for one doctor. */
export const getAvailability = ({start, end, timezone, doctorId}) => {
  const q = new URLSearchParams({start, end});
  if (timezone) q.set('timezone', timezone);
  if (doctorId) q.set('doctor_id', doctorId);
  return api.get(`/availability?${q}`).then((r) => (r.data && r.data.doctors) || []);
};

export const downloadAppointmentsPDF = (dateRange) => {
  const params = new URLSearchParams();
  if (dateRange && dateRange[0]) params.set('from', new Date(dateRange[0]).toISOString());
  if (dateRange && dateRange[1]) params.set('to', new Date(dateRange[1]).toISOString());
  return api.get(`/me/calendar/export.pdf?${params}`, {responseType: 'blob'}).then((r) => r.data);
};

export const getReminderHistory = (appointmentId) =>
  api.get(`/me/reminders${appointmentId ? `?appointment_id=${appointmentId}` : ''}`).then((r) => r.data);
